import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import '../components/ui/ui-button';
import { navigateBack } from '../router';
import { cardStyles, motionStyles, pageFrameStyles } from '../styles/lit-shared';

const BLOG_SKILLS = [
  { icon: '📐', title: 'Estructura narrativa', desc: 'Organiza el artículo en secciones con sentido: intro que engancha, desarrollo con profundidad, cierre con CTA suave.' },
  { icon: '🔎', title: 'SEO integrado', desc: 'Incluye las keywords del Director de forma natural. Sin forzarlas, sin repetirlas cinco veces en el primer párrafo.' },
  { icon: '📏', title: 'Extensión con sustancia', desc: 'Genera artículos de ~900 palabras. Si una sección queda corta, la regenera sola hasta que tiene lo suficiente.' },
  { icon: '📏', title: 'GEO integrado', desc: 'Adapta el contenido a los motores de búsqueda para aparecer en búsquedas a través de la IA.' },
];

const MARKETING_SKILLS = [
  { icon: '📸', title: 'Instagram', desc: 'Tono casual y visual. Emojis, hashtags relevantes y una llamada a la acción que invite a comentar o compartir.' },
  { icon: '💼', title: 'LinkedIn', desc: 'Tono ejecutivo. Datos, insights y una estructura que se lee bien en la home profesional sin parecer un comunicado de prensa.' },
  { icon: '👥', title: 'Facebook', desc: 'Conversacional y cercano. Una pregunta al final para que la gente comente, sin pasarse de informal.' },
  { icon: '⚡', title: 'X / Twitter', desc: 'Contundente y directo. 280 caracteres que dicen algo de verdad — sin relleno ni hashtag de adorno.' },
];

@customElement('agent-writers-page')
export class AgentWritersPage extends LitElement {
  private goBack() {
    navigateBack('/arquitectura');
  }

  static styles = [
    pageFrameStyles,
    cardStyles,
    motionStyles,
    css`
      .back-btn {
        margin-bottom: var(--space-xl);
      }

      .agent-header {
        margin-bottom: var(--space-2xl);
      }

      .agent-emoji {
        font-size: 3rem;
        line-height: 1;
        margin-bottom: var(--space-md);
        display: block;
      }

      .agent-title {
        font-size: clamp(2rem, 5vw, 2.75rem);
        font-weight: 700;
        letter-spacing: -0.03em;
        margin: 0 0 var(--space-sm);
        color: var(--color-text);
      }

      .agent-tagline {
        font-size: 1.125rem;
        color: var(--color-accent);
        font-weight: 500;
        margin: 0;
      }

      .section-title {
        font-size: 1.25rem;
        font-weight: 700;
        letter-spacing: -0.02em;
        margin: 0 0 var(--space-md);
        color: var(--color-text);
      }

      .subsection-title {
        font-size: 1.0625rem;
        font-weight: 600;
        margin: 0 0 var(--space-md);
        color: var(--color-text);
        display: flex;
        align-items: center;
        gap: var(--space-sm);
      }

      .what-section {
        margin-bottom: var(--space-2xl);
      }

      .what-section p {
        color: var(--color-text-muted);
        font-size: 1.0625rem;
        line-height: 1.7;
        margin: 0;
        max-width: 64ch;
      }

      .writers-block {
        margin-bottom: var(--space-2xl);
      }

      .writers-subsection {
        margin-bottom: var(--space-xl);
      }

      .skills-grid {
        display: grid;
        gap: var(--space-md);
        grid-template-columns: 1fr;
      }

      @media (min-width: 640px) {
        .skills-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }

      .skill-card {
        display: flex;
        gap: var(--space-md);
        align-items: flex-start;
      }

      .skill-icon {
        font-size: 1.5rem;
        flex-shrink: 0;
        line-height: 1;
        margin-top: 0.1rem;
      }

      .skill-card h3 {
        margin: 0 0 0.25rem;
        font-size: 0.9375rem;
        font-weight: 600;
        color: var(--color-text);
      }

      .skill-card p {
        margin: 0;
        font-size: 0.875rem;
        color: var(--color-text-muted);
        line-height: 1.55;
      }

      .parallel-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
        padding: 0.2rem 0.6rem;
        border-radius: var(--radius-full);
        background: rgb(255 255 255 / 6%);
        border: 1px solid var(--color-border);
        font-size: 0.75rem;
        font-weight: 600;
        letter-spacing: 0.04em;
        color: var(--color-text-muted);
        margin-bottom: var(--space-lg);
      }

      .model-note {
        padding: var(--space-lg);
        border-radius: var(--radius);
        background: var(--gradient-primary-soft);
        border: 1px solid var(--color-border-glow);
        font-size: 0.9375rem;
        color: var(--color-text-muted);
        line-height: 1.6;
        margin-bottom: var(--space-xl);
      }

      .model-note strong {
        color: var(--color-text);
      }
    `,
  ];

  render() {
    return html`
      <div class="animate-fade-up">
        <div class="back-btn">
          <ui-button variant="ghost" @ui-click=${this.goBack}>← Volver</ui-button>
        </div>

        <div class="agent-header">
          <span class="agent-emoji">✍️</span>
          <h1 class="agent-title">Los Writers</h1>
          <p class="agent-tagline">Dos especialistas. Uno escribe el artículo, el otro lo convierte en contenido para cada red.</p>
        </div>

        <div class="what-section">
          <h2 class="section-title">¿Qué hacen?</h2>
          <p>
            Los Writers son dos agentes que trabajan en paralelo — mientras el Blog Writer
            construye el artículo, el Marketing Writer ya está adaptando el mensaje para
            Instagram, LinkedIn, Facebook y Twitter. No se esperan el uno al otro. Cada uno
            tiene su especialidad y no se mete en el fregado del otro. El resultado: artículo
            y posts sociales listos al mismo tiempo, sin sacrificar calidad en ninguno de los dos.
          </p>
        </div>

        <span class="parallel-badge">⚡ Trabajan en paralelo — no en serie</span>

        <div class="writers-block">
          <div class="writers-subsection">
            <h2 class="subsection-title">📝 Blog Writer — El articulista</h2>
            <div class="skills-grid">
              ${BLOG_SKILLS.map(
                (s) => html`
                  <article class="card skill-card">
                    <span class="skill-icon">${s.icon}</span>
                    <div>
                      <h3>${s.title}</h3>
                      <p>${s.desc}</p>
                    </div>
                  </article>
                `,
              )}
            </div>
          </div>

          <div class="writers-subsection">
            <h2 class="subsection-title">📱 Marketing Writer — El especialista social</h2>
            <div class="skills-grid">
              ${MARKETING_SKILLS.map(
                (s) => html`
                  <article class="card skill-card">
                    <span class="skill-icon">${s.icon}</span>
                    <div>
                      <h3>${s.title}</h3>
                      <p>${s.desc}</p>
                    </div>
                  </article>
                `,
              )}
            </div>
          </div>
        </div>

        <div class="model-note">
          <strong>Modelo: Claude Sonnet 5</strong> — La joya de la corona en modelos de IA generativa y el auténtico motor de los Writers. Claude Sonnet 5 destaca porque combina una capacidad sobresaliente para mantener la coherencia narrativa en textos extensos con una memoria contextual superior: no pierde nunca el hilo, enlaza ideas de principio a fin y consigue que cada sección sume valor, sin repetirse ni desviarse. Es capaz de captar matices de estilo, modular el tono según la audiencia e integrar indicaciones complejas sin perder claridad ni frescura. <br>
          Para el Blog Writer, esto significa artículos profundos y homogéneos, de alta calidad periodística. <br>
          Y para el Marketing Writer, potencia la habilidad de reformular mensajes sin sonar repetitivo, adaptando el mismo contenido a cada red social con creatividad y originalidad. <br>
          Por todo esto, Claude Sonnet 5 es la piedra angular: asegura que el resultado brille tanto en la forma como en el fondo, superando los límites de los modelos tradicionales y asegurando una producción de primer nivel.
     
        </div>

        <ui-button href="https://blunticle.alejandrosn.com" variant="primary">Probar Blunticle</ui-button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agent-writers-page': AgentWritersPage;
  }
}
